import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import classNames from 'classnames';
import { List } from 'immutable';

// MUI
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MatchIcon from '@mui/icons-material/CompareArrows';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CircleIcon from '@mui/icons-material/Circle';
import FileDownload from '@mui/icons-material/GetApp';

// Companion-app-components
import { transactionsTypes } from 'companion-app-components/flux/transactions';
import { showReviewed } from 'companion-app-components/flux/preferences/selectors';

// Data
import { areMatchable, isUnacceptedScheduledTxn } from 'data/transactions/utils';

import useQCurrency from 'components/QCurrency/useQCurrency';
import QIconButton from 'components/QIconButton';
import { styles } from './styles';

const useStyles = makeStyles()(styles as Record<string, any>);

interface RegMEditBarProps {
  selectedTxns: List<transactionsTypes.CashFlowTransaction>;
  onEdit: () => void;
  onDelete: () => void;
  onDownload: () => void;
  onMatch: () => void;
  onReviewed: () => void;
  onNotReviewed: () => void;
  showReviewOptions: boolean;
  isWindowsDataset: boolean;
}

const RegMEditBar: React.FC<RegMEditBarProps> = ({
  selectedTxns,
  onEdit,
  onDelete,
  onMatch,
  onReviewed,
  onNotReviewed,
  onDownload,
  showReviewOptions = true,
  isWindowsDataset,
}) => {

  // SELECTORS ========================================================================
  const { classes } = useStyles();
  const showReview = useSelector(showReviewed);
  const { getSharedCurrency, formatAmount } = useQCurrency();

  const getTxnInfo = () => {
    let total = 0;
    let hasRecurring = false;
    selectedTxns?.forEach((item) => {
      total += Number(item.amount);
      hasRecurring = hasRecurring || isUnacceptedScheduledTxn(item);
    });
    return { total, hasRecurring };
  };

  const matchAvailable = () => {
    if (selectedTxns?.size !== 2) {
      return false;
    }
    const txns = selectedTxns.toArray();

    return areMatchable(txns[0], txns[1]);
  };

  const numSelected = selectedTxns?.size;
  const showMatch = matchAvailable();
  const txnInfo = getTxnInfo();

  const currSymbol = getSharedCurrency([selectedTxns]);
  const totalText = formatAmount(txnInfo.total, currSymbol);
  const selText = `${numSelected} Transaction${numSelected > 1 ? 's' : ''} Selected  (${totalText})`;
  const unreviewedTxnsSelected = selectedTxns?.some((txn) => !txn.isReviewed);

  return (
    <div className={classes.container}>
      <div className={classes.iconContainer}>
        {showMatch && (
          <QIconButton
            aria-label="Match transactions"
            id="bulk-edit-ui-match"
            IconComponent={MatchIcon}
            onClick={onMatch}
            tooltip={'Match the downloaded transaction to the manual transaction'}
            size="small-target"
          />
        )}

        {!txnInfo.hasRecurring && (
          <QIconButton
            aria-label="Edit selected transactions"
            id="bulk-edit-ui-edit"
            IconComponent={EditIcon}
            onClick={onEdit}
            tooltip="Bulk edit all selected transactions at once"
            size="small-target"
          />
        )}

        <QIconButton
          aria-label="Delete selected transactions"
          id="bulk-edit-ui-delete"
          IconComponent={DeleteIcon}
          onClick={onDelete}
          tooltip="Delete all selected transactions"
          size="small-target"
        />

        {!txnInfo.hasRecurring && showReview && (
          <>
            {showReviewOptions && (unreviewedTxnsSelected || !isWindowsDataset) && <QIconButton
              aria-label="Mark selected transactions as reviewed"
              id="bulk-edit-ui-mark-as-reviewed"
              IconComponent={isWindowsDataset ? CheckCircleOutlineIcon : RadioButtonUncheckedIcon}
              IconProps={{
                className: classNames(classes.reviewed, isWindowsDataset ? 'qwinReviewed' : 'reviewed'),
              }}
              onClick={onReviewed}
              tooltip="Mark all selected transactions reviewed"
              size="small-target"
            />}
            { !isWindowsDataset && <QIconButton
              aria-label="Mark all selected transactions as not reviewed"
              id="bulk-edit-ui-mark-as-not-reviewed"
              IconComponent={CircleIcon}
              IconProps={{ className: classNames(classes.reviewed, 'notReviewed') }}
              onClick={onNotReviewed}
              tooltip="Mark all selected transactions as not reviewed"
              size="small-target"
            />}
          </>
        )}

        <QIconButton
          aria-label="Download a CSV file with all selected transactions"
          id="bulk-edit-ui-mark-as-reviewed"
          IconComponent={FileDownload}
          onClick={onDownload}
          tooltip="Download a CSV file with all selected transactions"
          size="small-target"
        />
      </div>
      <Typography variant="subtitle1" className={classes.text} id="reg-header-selected-total">
        {selText}
      </Typography>
    </div>
  );
};

export default RegMEditBar;
